import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { DisconnectionAlert } from '@/components/DisconnectionAlert';
import { Heartbeat } from '@/components/Heartbeat';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import LeadsPage from '@/pages/LeadsPage';
import OperatorsPage from '@/pages/OperatorsPage';
import TagsPage from '@/pages/TagsPage';
import SalesPage from '@/pages/SalesPage';
import WhatsAppPage from '@/pages/WhatsAppPage';
import WhatsAppConfigPage from '@/pages/WhatsAppConfigPage';
import AIAssistantPage from '@/pages/AIAssistantPage';
import ProductsPage from '@/pages/ProductsPage';
import LeadDetailPage from '@/pages/LeadDetailPage';
import PerformancePage from '@/pages/PerformancePage';
import WebhookLogsPage from '@/pages/WebhookLogsPage';
import BroadcastPage from '@/pages/BroadcastPage';
import InstancesPage from '@/pages/InstancesPage';
import ImportPage from '@/pages/ImportPage';
import WhatsAppRecoveryPage from '@/pages/WhatsAppRecoveryPage';
import ReengagementPage from '@/pages/ReengagementPage';
import '@/App.css';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'sans-serif', background: '#f8fafc' }}>
          <h2 style={{ color: '#0f766e', marginBottom: 8 }}>Erro ao carregar a pagina</h2>
          <p style={{ color: '#64748b', marginBottom: 16 }}>Ocorreu um erro inesperado.</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = '/dashboard'; }} style={{ padding: '8px 24px', background: '#0f766e', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' }}>
            Voltar ao Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" />;

  // Forcar troca de senha no primeiro login para admin/supervisor
  if (user?.must_change_password) {
    return <ChangePasswordPage />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/leads"
        element={
          <PrivateRoute>
            <LeadsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/operators"
        element={
          <PrivateRoute>
            <OperatorsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tags"
        element={
          <PrivateRoute>
            <TagsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <PrivateRoute>
            <SalesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/whatsapp"
        element={
          <PrivateRoute>
            <WhatsAppPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/whatsapp-config"
        element={
          <PrivateRoute>
            <WhatsAppConfigPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/ai-assistant"
        element={
          <PrivateRoute>
            <AIAssistantPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/products"
        element={
          <PrivateRoute>
            <ProductsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <PrivateRoute>
            <PerformancePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/webhook-logs"
        element={
          <PrivateRoute>
            <WebhookLogsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/whatsapp-recovery"
        element={
          <PrivateRoute>
            <WhatsAppRecoveryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/reengagement"
        element={
          <PrivateRoute>
            <ReengagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/broadcast"
        element={
          <PrivateRoute>
            <BroadcastPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/instances"
        element={
          <PrivateRoute>
            <InstancesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/import"
        element={
          <PrivateRoute>
            <ImportPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <PrivateRoute>
            <LeadDetailPage />
          </PrivateRoute>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Heartbeat />
          <DisconnectionAlert />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
